/**
 * Defensive readers for `Node.props` (typed `Record<string, unknown>` on the
 * wire). An app that sends the wrong shape gets a degraded node, never a
 * shell-level crash.
 */

export function str(props: Record<string, unknown>, key: string, fallback = ""): string {
  const value = props[key];
  return typeof value === "string" ? value : fallback;
}

export function optStr(props: Record<string, unknown>, key: string): string | undefined {
  const value = props[key];
  return typeof value === "string" ? value : undefined;
}

export function num(props: Record<string, unknown>, key: string, fallback: number): number {
  const value = props[key];
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

export function optNum(props: Record<string, unknown>, key: string): number | undefined {
  const value = props[key];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

export function bool(props: Record<string, unknown>, key: string, fallback = false): boolean {
  const value = props[key];
  return typeof value === "boolean" ? value : fallback;
}

export function oneOf<T extends string>(
  props: Record<string, unknown>,
  key: string,
  allowed: readonly T[],
  fallback: T,
): T {
  const value = props[key];
  return typeof value === "string" && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

export type Option = { value: string; label: string };

export function options(props: Record<string, unknown>, key: string): Option[] {
  const value = props[key];
  if (!Array.isArray(value)) return [];
  const out: Option[] = [];
  for (const item of value) {
    if (typeof item !== "object" || item === null) continue;
    const record = item as Record<string, unknown>;
    const optionValue = record["value"];
    if (typeof optionValue !== "string") continue;
    const label = record["label"];
    out.push({ value: optionValue, label: typeof label === "string" ? label : optionValue });
  }
  return out;
}

/** px value from a token-multiple prop, clamped so an app cannot blow up layout. */
export function space(props: Record<string, unknown>, key: string, fallback = 0): number {
  return Math.max(0, Math.min(96, num(props, key, fallback)));
}
